import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  Cpu,
  HardDrive,
  MemoryStick,
  RefreshCw,
  Server,
} from "lucide-react";

import PageHeader from "../../components/PageHeader";
import { COLORS } from "../../constants";
import { servidorOpsService } from "../../services/servidorOpsService";

function pct(ratio: number | null | undefined): string {
  if (ratio == null || Number.isNaN(ratio)) return "—";
  return `${(ratio * 100).toFixed(1)}%`;
}

function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatUptime(seconds: number | null | undefined): string {
  if (seconds == null || seconds < 0) return "—";
  const s = Math.floor(seconds);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function toneForRatio(ratio: number | null | undefined): string {
  if (ratio == null || Number.isNaN(ratio)) return COLORS.NEUTRAL_300;
  if (ratio >= 0.85) return COLORS.ERROR_DARK;
  if (ratio >= 0.7) return COLORS.WARNING_DARK;
  return COLORS.SUCCESS;
}

const UsageBar: React.FC<{ ratio: number | null | undefined }> = ({
  ratio,
}) => {
  const width =
    ratio == null || Number.isNaN(ratio)
      ? 0
      : Math.min(100, Math.max(0, ratio * 100));
  return (
    <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-neutral-100">
      <div
        className="h-full rounded-full transition-[width] duration-500"
        style={{ width: `${width}%`, background: toneForRatio(ratio) }}
      />
    </div>
  );
};

const MetricCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  value: string;
  hint?: string;
  ratio?: number | null;
  footer?: React.ReactNode;
}> = ({ icon, title, value, hint, ratio, footer }) => (
  <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">
          {title}
        </p>
        <p className="mt-1 truncate text-2xl font-semibold tabular-nums text-neutral-900">
          {value}
        </p>
        {hint ? (
          <p className="mt-1 text-xs text-neutral-500">{hint}</p>
        ) : null}
      </div>
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
        style={{ background: `${COLORS.PRIMARY}14`, color: COLORS.PRIMARY }}
      >
        {icon}
      </div>
    </div>
    {ratio !== undefined ? <UsageBar ratio={ratio} /> : null}
    {footer ? (
      <div className="mt-3 border-t border-neutral-100 pt-3 text-xs text-neutral-600">
        {footer}
      </div>
    ) : null}
  </div>
);

const MonitoramentoServidor: React.FC = () => {
  const { data, isLoading, isFetching, dataUpdatedAt, refetch } = useQuery({
    queryKey: ["desenvolvedor", "monitoring", "snapshot"],
    queryFn: () => servidorOpsService.getMonitoringSnapshot(),
    refetchInterval: 30_000,
  });

  const updatedLabel = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString("pt-BR", {
        timeZone: "America/Sao_Paulo",
      })
    : null;

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-4 md:p-6">
      <PageHeader
        title="Monitoramento do servidor"
        description="CPU, memória, load e discos do host (perfil Desenvolvedor)."
        actions={
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-white disabled:opacity-60"
            style={{ background: COLORS.PRIMARY }}
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw size={16} className={isFetching ? "animate-spin" : ""} />
            Atualizar
          </button>
        }
      />

      {isLoading || !data ? (
        <p className="text-sm text-neutral-500">Carregando…</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-neutral-200 bg-white px-4 py-3 text-xs text-neutral-600 shadow-sm">
            <span className="inline-flex items-center gap-1.5">
              <Server size={14} style={{ color: COLORS.PRIMARY }} />
              <span className="font-medium text-neutral-800">
                {data.hostname ?? "host"}
              </span>
            </span>
            <span>Node {data.nodeVersion}</span>
            <span>Uptime pod {formatUptime(data.uptimeSeconds)}</span>
            {updatedLabel ? <span>Atualizado às {updatedLabel}</span> : null}
            <span
              className={`rounded-full px-2 py-0.5 font-medium ${
                data.hostRootMounted
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-amber-50 text-amber-800"
              }`}
            >
              {data.hostRootMounted ? "métricas do host" : "somente pod"}
            </span>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <MetricCard
              icon={<Cpu size={18} />}
              title="CPU"
              value={pct(data.cpu?.usedRatio)}
              hint={
                data.cpu?.cores != null
                  ? `${data.cpu.cores} núcleo${data.cpu.cores === 1 ? "" : "s"}`
                  : "uso instantâneo"
              }
              ratio={data.cpu?.usedRatio}
              footer={
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  <span>
                    Load/core:{" "}
                    {data.cpu?.loadPerCore != null
                      ? data.cpu.loadPerCore.toFixed(2)
                      : "—"}
                  </span>
                  <span>
                    Amostra: {data.cpu?.sampleMs != null ? `${data.cpu.sampleMs}ms` : "—"}
                  </span>
                </div>
              }
            />

            <MetricCard
              icon={<MemoryStick size={18} />}
              title="Memória"
              value={pct(data.memory?.usedRatio)}
              hint={`${formatBytes(data.memory?.usedBytes)} / ${formatBytes(data.memory?.totalBytes)}`}
              ratio={data.memory?.usedRatio}
              footer={
                <span>
                  Livre: {formatBytes(data.memory?.availableBytes)}
                </span>
              }
            />

            <MetricCard
              icon={<Activity size={18} />}
              title="Load average"
              value={data.loadavg?.load1?.toFixed(2) ?? "—"}
              hint="1 / 5 / 15 minutos"
              footer={
                <span className="tabular-nums">
                  {data.loadavg?.load1?.toFixed(2) ?? "—"}
                  {" · "}
                  {data.loadavg?.load5?.toFixed(2) ?? "—"}
                  {" · "}
                  {data.loadavg?.load15?.toFixed(2) ?? "—"}
                </span>
              }
            />
          </div>

          <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <HardDrive size={16} style={{ color: COLORS.PRIMARY }} />
              <h2 className="text-sm font-semibold text-neutral-900">Discos</h2>
            </div>
            <ul className="space-y-4">
              {(data.disks || []).map((d) => (
                <li key={d.path}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                    <div>
                      <span className="font-medium text-neutral-800">
                        {d.label}
                      </span>
                      <span className="ml-2 text-xs text-neutral-400">
                        {d.path}
                      </span>
                    </div>
                    <span className="tabular-nums text-neutral-700">
                      {formatBytes(d.usedBytes)} / {formatBytes(d.totalBytes)}{" "}
                      <span className="text-neutral-500">
                        ({pct(d.usedRatio)})
                      </span>
                    </span>
                  </div>
                  <UsageBar ratio={d.usedRatio} />
                </li>
              ))}
              {(data.disks || []).length === 0 ? (
                <li className="text-sm text-neutral-500">Nenhum disco reportado.</li>
              ) : null}
            </ul>
          </div>

          <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold text-neutral-900">
              Diretórios
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[320px] text-left text-sm">
                <thead>
                  <tr className="border-b border-neutral-100 text-xs uppercase tracking-wide text-neutral-500">
                    <th className="pb-2 font-medium">Nome</th>
                    <th className="pb-2 font-medium">Caminho</th>
                    <th className="pb-2 text-right font-medium">Tamanho</th>
                  </tr>
                </thead>
                <tbody>
                  {data.dirs.map((d) => (
                    <tr
                      key={d.path}
                      className="border-b border-neutral-50 last:border-0"
                    >
                      <td className="py-2.5 font-medium text-neutral-800">
                        {d.label}
                        {!d.exists ? (
                          <span className="ml-2 text-xs font-normal text-amber-600">
                            ausente
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2.5 text-xs text-neutral-400">
                        {d.path}
                      </td>
                      <td className="py-2.5 text-right tabular-nums text-neutral-700">
                        {formatBytes(d.sizeBytes)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MonitoramentoServidor;
